import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Highlight,
  IconBannersWarning,
  VStack,
  Text,
  CharacterTextInput,
  HStack,
  Tooltip,
} from '@adc/parallax-component-library';
import i18n from '../../utilities/i18n';
import mediator from '../../utilities/mediator';
import { useLocation, useNavigate } from 'react-router';
import { MFAChallengeResponse } from 'adc-sirius-module-common';
import { useAppSelector } from '../../reducers/hooks';
import { ROUTES } from '../../constants';

export default function MultiFactorAuth() {
  const location = useLocation();
  const navigate = useNavigate();
  const authState = useAppSelector((state) => state.auth);
  const [mfaConfig, setMfaConfig] = useState<MFAChallengeResponse | null>(null);

  const [isValidMfaSession, setIsValidMfaSession] = useState(true);

  const minutesUntilResendAvailable = useMemo(() => {
    return Math.max(
      0,
      Math.ceil(((mfaConfig?.resendAllowedAt ?? 0) - Date.now()) / 60_000),
    );
  }, [mfaConfig?.resendAllowedAt]);

  const [code, setCode] = useState('');
  const [canResend, setCanResend] = useState(true);
  const [locked, setLocked] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    const { codeLength, expiresAt, resendAllowedAt } =
      location.state || ({} as MFAChallengeResponse);

    if (!codeLength || !expiresAt || !resendAllowedAt || !authState.token) {
      setIsValidMfaSession(false);
      setErrorMessage(
        i18n.t('Auth_MultiFactorAuthentication.error_invalid_session'),
      );
    } else {
      setMfaConfig(location.state as MFAChallengeResponse);
    }
  }, [location.state, authState.token]);

  const verifyCode = () => {
    setErrorMessage('');

    setIsVerifying(true);
    return mediator
      .request('auth:mfaVerify', { code })
      .catch((error) => {
        if (error.status === 400) {
          const mfaResponse = error.error?.details as MFAChallengeResponse;

          setMfaConfig(mfaResponse);
          setErrorMessage(
            i18n.t('Auth_MultiFactorAuthentication.error_wrong_code'),
          );
        } else if (error.status === 401) {
          setErrorMessage(
            i18n.t('Auth_MultiFactorAuthentication.error_unauthorized'),
          );
        } else if (error.status === 429) {
          const mfaResponse = error.error?.details as MFAChallengeResponse;

          setMfaConfig(mfaResponse);

          if (mfaResponse.lockedUntil) {
            setErrorMessage(
              i18n.t('Auth_MultiFactorAuthentication.error_too_many_attempts', {
                date: new Date(mfaResponse.lockedUntil).toLocaleTimeString(),
              }),
            );
          } else {
            setErrorMessage(
              i18n.t(
                'Auth_MultiFactorAuthentication.error_too_many_attempts_generic',
              ),
            );
          }
        } else {
          setErrorMessage(
            i18n.t('Auth_MultiFactorAuthentication.error_generic'),
          );
        }
      })
      .finally(() => setIsVerifying(false));
  };

  const resendCode = () => {
    setIsResending(true);
    setErrorMessage('');

    return mediator
      .request<MFAChallengeResponse>('auth:resendCode')
      .then((response: MFAChallengeResponse) => {
        setMfaConfig(response);
      })
      .catch(() =>
        setErrorMessage(
          i18n.t('Auth_MultiFactorAuthentication.error_resend_code'),
        ),
      )
      .finally(() => setIsResending(false));
  };

  useEffect(() => {
    if (mfaConfig?.resendAllowedAt) {
      if (mfaConfig.resendAllowedAt > Date.now()) {
        setCanResend(false);
        const timer = window.setTimeout(() => {
          setCanResend(true);
        }, mfaConfig.resendAllowedAt - Date.now());

        return () => window.clearTimeout(timer);
      } else {
        setCanResend(true);
      }
    }
  }, [mfaConfig?.resendAllowedAt]);

  useEffect(() => {
    if (mfaConfig?.lockedUntil) {
      if (mfaConfig.lockedUntil > Date.now()) {
        setLocked(true);
        const timer = window.setTimeout(() => {
          setLocked(false);
        }, mfaConfig.lockedUntil - Date.now());

        return () => window.clearTimeout(timer);
      } else {
        setLocked(false);
      }
    }
  }, [mfaConfig?.lockedUntil]);

  const resendButton = (
    <Button
      onPress={resendCode}
      label={i18n.t('Auth_MultiFactorAuthentication.resend')}
      variant="secondary"
      disabled={!canResend || isResending}
      testID="mfa-resend-button"
    />
  );

  return (
    <VStack
      className="align-center"
      minWidth="50%"
      maxWidth="100%"
      padding="$8"
    >
      <Card>
        <VStack alignItems="center" gap="$4">
          <Text type="$h2.strong">
            {i18n.t('Auth_MultiFactorAuthentication.title')}
          </Text>
          {errorMessage ? (
            <Highlight
              variant="danger"
              iconStart={
                <IconBannersWarning
                  color="$neutral.0"
                  accentColor="$support.danger.default"
                />
              }
              title={i18n.t('Common.errorGenericTitle')}
              body={errorMessage}
              testID="mfa-error-banner"
            />
          ) : null}
          {mfaConfig && isValidMfaSession ? (
            <>
              <CharacterTextInput
                label={i18n.t('Auth_MultiFactorAuthentication.code')}
                aria-label={i18n.t('Auth_MultiFactorAuthentication.code')}
                inputType="numeric"
                length={mfaConfig.codeLength}
                value={code}
                onValueChange={(value) => setCode(value)}
                testID="mfa-code-input"
              />
              <Text type="$bodySmall.default" testID="mfa-expiration-message">
                {i18n.t('Auth_MultiFactorAuthentication.expiration', {
                  expiresAt: new Date(mfaConfig.expiresAt).toLocaleTimeString(),
                })}
              </Text>
              <Text type="$bodySmall.default" testID="mfa-resend-message">
                {i18n.t(
                  `Auth_MultiFactorAuthentication.message_${minutesUntilResendAvailable === 1 ? '1' : '0'}`,
                  {
                    count: minutesUntilResendAvailable,
                  },
                )}
              </Text>
              <HStack gap="$2" alignItems="center">
                <Button
                  onPress={verifyCode}
                  label={i18n.t('Common.continue')}
                  disabled={
                    code.length !== mfaConfig.codeLength ||
                    locked ||
                    isVerifying
                  }
                  testID="mfa-verify-button"
                />
                {canResend ? (
                  resendButton
                ) : (
                  <Tooltip
                    text={i18n.t(
                      'Auth_MultiFactorAuthentication.resendTooltip',
                      {
                        time: new Date(
                          mfaConfig.resendAllowedAt,
                        ).toLocaleTimeString(),
                      },
                    )}
                  >
                    {resendButton}
                  </Tooltip>
                )}
              </HStack>
            </>
          ) : (
            <Button
              variant="secondary"
              label={i18n.t('Auth_MultiFactorAuthentication.login')}
              onPress={() => navigate(ROUTES.AUTH.LOGIN)}
              testID="mfa-login-button"
            />
          )}
        </VStack>
      </Card>
    </VStack>
  );
}
